import { Caption1, tokens } from "@fluentui/react-components";
import { useEffect, useMemo, useState } from "react";
import { AppDialog, DialogChoiceGroup, DialogField, DialogGrid2 } from "@/components/app-dialog";
import { FuiButton, FuiDropdown, FuiInput, FuiTextarea } from "@/components/fui-control";
import type { IntakeItem, IntakePriority, IntakeType } from "@/engine/types";
import { usePlanSurface } from "@/features/access/use-plan-access";
import {
  INTAKE_PRIORITIES,
  INTAKE_PRIORITY_LABEL,
  INTAKE_SOURCE_LABEL,
  INTAKE_STATUS_LABEL,
  INTAKE_TYPE_LABEL,
  INTAKE_TYPES,
  intakeCode,
  isOpenIntake,
} from "@/store/intake-ops";
import { useProjectStore } from "@/store/project-store";

type Mapping = "new" | "merge";

const typeOptions = INTAKE_TYPES.map((v) => ({ value: v, label: INTAKE_TYPE_LABEL[v] }));
const priorityOptions = INTAKE_PRIORITIES.map((v) => ({ value: v, label: INTAKE_PRIORITY_LABEL[v] }));

function num(v: string): number | undefined {
  if (v.trim() === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/** 요구를 심사하고, 새 작업으로 만들거나 진행 중인 작업에 반영한다. */
export function IntakeTriageDialog({ intakeId, onClose }: { intakeId: string | null; onClose: () => void }) {
  const surface = usePlanSurface();
  const intake = useProjectStore((s) => s.project.intakes?.find((i) => i.id === intakeId));
  const tasks = useProjectStore((s) => s.project.tasks);
  const schedule = useProjectStore((s) => s.derived.schedule);
  const buckets = useProjectStore((s) => s.project.planner.buckets);
  const execTasks = useProjectStore((s) => s.project.executionTasks);
  const s = useProjectStore.getState;

  const [type, setType] = useState<IntakeType>("newScope");
  const [priority, setPriority] = useState<IntakePriority>("medium");
  const [effort, setEffort] = useState("");
  const [impact, setImpact] = useState("");
  const [criteria, setCriteria] = useState("");
  const [note, setNote] = useState("");
  const [mapping, setMapping] = useState<Mapping>("new");
  const [wbsId, setWbsId] = useState("");
  const [bucketId, setBucketId] = useState("");
  const [targetTaskId, setTargetTaskId] = useState("");

  useEffect(() => {
    if (!intake) return;
    setType(intake.type);
    setPriority(intake.priority);
    setEffort(intake.estimatedEffortMM?.toString() ?? "");
    setImpact(intake.scheduleImpactDays?.toString() ?? "");
    setCriteria(intake.acceptanceCriteria.join("\n"));
    setNote(intake.reviewNote ?? "");
    setWbsId(intake.targetScheduleTaskId ?? "");
    setTargetTaskId(intake.targetExecutionTaskId ?? "");
    setMapping(intake.type === "bug" || intake.type === "specChange" ? "merge" : "new");
    setBucketId("");
    // 다른 요구를 열 때만 초안을 다시 채운다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intake?.id]);

  const wbsOptions = useMemo(
    () => [
      { value: "none", label: "연결 안 함" },
      ...(schedule?.order ?? []).map((id) => ({
        value: id,
        label: `${schedule!.tasks.get(id)?.wbs ?? ""} ${tasks.find((t) => t.id === id)?.name ?? ""}`.trim(),
      })),
    ],
    [schedule, tasks],
  );
  const bucketOptions = useMemo(
    () => [{ value: "none", label: "없음" }, ...buckets.map((b) => ({ value: b.id, label: b.name }))],
    [buckets],
  );
  const taskOptions = useMemo(
    () =>
      execTasks.map((t) => ({
        value: t.id,
        label: t.status === "done" ? `${t.title || "제목 없음"} · 완료` : t.title || "제목 없음",
      })),
    [execTasks],
  );

  if (!intakeId || !intake) return null;
  const open = isOpenIntake(intake);
  const editable = open && surface.editIssue;

  const criteriaList = criteria
    .split("\n")
    .map((c) => c.trim())
    .filter(Boolean);
  const draftPatch = (): Partial<IntakeItem> => ({
    type,
    priority,
    acceptanceCriteria: criteriaList,
    ...(wbsId ? { targetScheduleTaskId: wbsId } : {}),
    ...(num(effort) !== undefined ? { estimatedEffortMM: num(effort) } : {}),
    ...(num(impact) !== undefined ? { scheduleImpactDays: num(impact) } : {}),
  });
  const accept = () => {
    s().updateIntake(intake.id, draftPatch());
    const opts = { note, estimatedEffortMM: num(effort), scheduleImpactDays: num(impact) };
    if (mapping === "new") {
      const id = s().acceptIntakeAsTask(intake.id, { ...opts, linkedTaskIds: wbsId ? [wbsId] : [], ...(bucketId ? { bucketId } : {}) });
      if (id) onClose();
    } else if (targetTaskId && s().mergeIntakeIntoTask(intake.id, targetTaskId, opts)) {
      onClose();
    }
  };
  const closeAs = (status: "rejected" | "deferred") => {
    if (!note.trim()) return;
    s().closeIntake(intake.id, status, note);
    onClose();
  };
  const linkedId = intake.createdExecutionTaskId ?? intake.targetExecutionTaskId;
  const canAccept = mapping === "new" || !!targetTaskId;
  const showFooter = editable || (!open && surface.editIssue && intake.status !== "accepted");

  return (
    <AppDialog
      open
      onClose={onClose}
      title={`${intakeCode(intake.seq)} · ${intake.title || "제목 없음"}`}
      width="lg"
      footer={
        showFooter ? (
          <>
            {editable && intake.status === "pending" && (
              <FuiButton density="dialog" appearance="secondary" onClick={() => s().startIntakeReview(intake.id)}>
                심사 시작
              </FuiButton>
            )}
            {editable && (
              <FuiButton density="dialog" appearance="secondary" disabled={!note.trim()} onClick={() => closeAs("deferred")}>
                보류
              </FuiButton>
            )}
            {editable && (
              <FuiButton density="dialog" appearance="secondary" disabled={!note.trim()} onClick={() => closeAs("rejected")}>
                반려
              </FuiButton>
            )}
            {editable && (
              <FuiButton density="dialog" appearance="primary" disabled={!canAccept} onClick={accept}>
                {mapping === "new" ? "작업 만들기" : "작업에 반영"}
              </FuiButton>
            )}
            {!open && surface.editIssue && intake.status !== "accepted" && (
              <FuiButton density="dialog" appearance="primary" onClick={() => s().reopenIntake(intake.id)}>
                다시 심사
              </FuiButton>
            )}
          </>
        ) : undefined
      }
    >
      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
        {INTAKE_SOURCE_LABEL[intake.source]}
        {intake.organization ? ` · ${intake.organization}` : ""} · {intake.author || "작성자 없음"} · {INTAKE_STATUS_LABEL[intake.status]}
      </Caption1>
      {intake.description ? <div style={{ whiteSpace: "pre-wrap" }}>{intake.description}</div> : null}

      <DialogGrid2>
        <DialogField label="종류">
          <FuiDropdown density="dialog" value={type} disabled={!editable} options={typeOptions} onValueChange={(v) => setType(v as IntakeType)} />
        </DialogField>
        <DialogField label="우선순위">
          <FuiDropdown density="dialog" value={priority} disabled={!editable} options={priorityOptions} onValueChange={(v) => setPriority(v as IntakePriority)} />
        </DialogField>
      </DialogGrid2>
      <DialogGrid2>
        <DialogField label="예상 공수 (M/M)">
          <FuiInput density="dialog" type="number" min={0} step={0.1} value={effort} disabled={!editable} onChange={(_, d) => setEffort(d.value)} />
        </DialogField>
        <DialogField label="일정 영향 (일)">
          <FuiInput density="dialog" type="number" min={0} value={impact} disabled={!editable} onChange={(_, d) => setImpact(d.value)} />
        </DialogField>
      </DialogGrid2>

      <DialogField label="검수 기준" hint="한 줄에 하나. 작업을 만들면 검수 항목이 됩니다.">
        <FuiTextarea
          value={criteria}
          disabled={!editable}
          onChange={(_, d) => setCriteria(d.value)}
          rows={4}
          placeholder={"견적서에 부가세 행이 보인다\n합계는 공급가와 부가세를 더한 값이다"}
        />
      </DialogField>

      {editable && (
        <DialogChoiceGroup
          label="반영"
          columns={2}
          cards
          value={mapping}
          onChange={setMapping}
          options={[
            { value: "new", label: "새 작업", hint: "검수 기준이 항목이 됩니다. 고객 요청이면 고객 인수도 붙습니다." },
            { value: "merge", label: "기존 작업", hint: "고른 작업에 요구 반영 검수를 붙입니다. 끝난 작업은 다시 진행 중이 됩니다." },
          ]}
        />
      )}
      {editable && mapping === "new" && (
        <DialogGrid2>
          <DialogField label="연결할 일정">
            <FuiDropdown density="dialog" value={wbsId || "none"} options={wbsOptions} onValueChange={(v) => setWbsId(v === "none" ? "" : v)} />
          </DialogField>
          <DialogField label="버킷">
            <FuiDropdown density="dialog" value={bucketId || "none"} options={bucketOptions} onValueChange={(v) => setBucketId(v === "none" ? "" : v)} />
          </DialogField>
        </DialogGrid2>
      )}
      {editable && mapping === "merge" && (
        <DialogField label="반영할 작업" required>
          <FuiDropdown
            density="dialog"
            value={targetTaskId}
            placeholder="작업 선택"
            options={taskOptions}
            onValueChange={setTargetTaskId}
          />
        </DialogField>
      )}

      {(editable || intake.reviewNote) && (
        <DialogField label="심사 의견" hint={editable ? "보류와 반려에는 의견이 필요합니다." : undefined}>
          <FuiTextarea value={note} disabled={!editable} onChange={(_, d) => setNote(d.value)} rows={2} />
        </DialogField>
      )}
      {linkedId && (
        <FuiButton
          appearance="subtle"
          density="dialog"
          onClick={() => {
            s().setExecutionFocus(linkedId);
            onClose();
          }}
        >
          만든 작업 열기
        </FuiButton>
      )}
    </AppDialog>
  );
}
